import { Box, CircularProgress } from '@mui/material';
import { createContext, useContext, useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { setAuthErrorHandler, setTokenProvider, type AuthErrorKind } from '../api/client';
import { cognitoLogoutUrl, getUserManager } from './authConfig';
import { ForbiddenScreen, IdentityProviderUnavailableScreen } from './AuthErrorBoundary';

type AuthStatus = 'authenticating' | 'authenticated' | 'forbidden' | 'identityProviderUnavailable';

interface AuthContextValue {
  readonly status: AuthStatus;
  readonly displayName: string | null;
  readonly logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthState(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthState: ausserhalb von AuthProvider verwendet.');
  }
  return ctx;
}

/**
 * business-logic-model-web-oberflaeche, Abschnitt "Authentifizierung —
 * geschlossene Lücke": Bearer-Token-Pfad via Authorization-Code+PKCE gegen
 * Cognito (oidc-client-ts). Das ID-Token geht als Bearer an die rest-api;
 * Erneuerung per Refresh-Token, nur bei Fehlschlag interaktiver
 * Redirect-Fallback (security-design-web-oberflaeche).
 */
export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('authenticating');
  const [forbiddenMessage, setForbiddenMessage] = useState<string | undefined>(undefined);
  const [displayName, setDisplayName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap(): Promise<void> {
      const userManager = getUserManager();
      const params = new URLSearchParams(window.location.search);
      if (params.has('code') && params.has('state')) {
        await userManager.signinRedirectCallback();
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      const user = await userManager.getUser();
      if (!user || user.expired) {
        await userManager.signinRedirect();
        return; // navigiert weg
      }
      setDisplayName(user.profile.name ?? user.profile.email ?? null);
      if (!cancelled) {
        setStatus('authenticated');
      }
    }

    bootstrap().catch(() => {
      if (!cancelled) {
        setStatus('identityProviderUnavailable');
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setTokenProvider(async () => {
      const userManager = getUserManager();
      let user = await userManager.getUser();
      if (!user || user.expired) {
        try {
          user = await userManager.signinSilent();
        } catch (error) {
          await userManager.signinRedirect();
          throw error;
        }
      }
      if (!user?.id_token) {
        throw new Error('AuthProvider: kein ID-Token verfügbar.');
      }
      return user.id_token;
    });

    setAuthErrorHandler((kind: AuthErrorKind, message: string | undefined) => {
      if (kind === 'unauthorized') {
        void getUserManager().signinRedirect();
        return;
      }
      if (kind === 'forbidden') {
        setForbiddenMessage(message);
        setStatus('forbidden');
        return;
      }
      setStatus('identityProviderUnavailable');
    });
  }, []);

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      status,
      displayName,
      logout: () => {
        void getUserManager()
          .removeUser()
          .then(() => window.location.assign(cognitoLogoutUrl()));
      },
    }),
    [status, displayName],
  );

  if (status === 'authenticating') {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh" data-testid="auth-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'forbidden') {
    return <ForbiddenScreen message={forbiddenMessage} />;
  }

  if (status === 'identityProviderUnavailable') {
    return <IdentityProviderUnavailableScreen />;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
